import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { CONTACT_NAME, CONTACT_EMAIL } from "../config";
import {
  FaChartLine,
  FaCode,
  FaShieldAlt,
  FaBriefcase,
  FaGraduationCap,
} from "react-icons/fa";
import RevealOnScroll from "../components/RevealOnScroll";
import EnhancedButton from "../components/EnhancedButton";

const timeline = [
  {
    year: "2015–2022",
    title: "Financial Analyst → Senior Financial Analyst, NVA",
    icon: FaChartLine,
    description:
      "Built reusable screening models across 400+ acquisition targets at National Veterinary Associates and trained analyst teams on a shared playbook so outputs were comparable.",
  },
  {
    year: "2022–2024",
    title: "M&A Manager → Director of M&A, Destination Pet",
    icon: FaBriefcase,
    description:
      "Completed 100+ business valuations, then led a team of six through the acquisition and integration of 67 veterinary locations.",
  },
  {
    year: "2024–2025",
    title: "Full-Stack Web3 Bootcamp, Metana",
    icon: FaGraduationCap,
    description:
      "Intensive full-stack and Solidity training while consulting on M&A. Started shipping production web apps with tests, CI, and live demos.",
  },
  {
    year: "2024–Present",
    title: "Director of M&A, VetEvolve",
    icon: FaCode,
    description:
      "Own the valuation models used by the investment committee. Outside of deals, I ship full-stack products: Waxmark, a scanned registry for AI instruction files; a wedding site with RSVP management; and OddScout, a Chainlink-backed odds oracle.",
  },
];

const proofPillars = [
  {
    icon: FaChartLine,
    title: "Finance Background",
    body: "11 years in healthcare M&A — valuations, Quality of Earnings, risk assessment, and stakeholder management at scale.",
  },
  {
    icon: FaCode,
    title: "Engineering Craft",
    body: "TypeScript, React, Next.js, Node.js, and Postgres apps shipped with tests, CI, and live demos. Solidity and Rust when the work is onchain.",
  },
  {
    icon: FaShieldAlt,
    title: "Security Mindset",
    body: "Due diligence discipline applied to software: Waxmark scans every submission for prompt injection and leaked secrets, and CI gates block untested code.",
  },
];

function About() {
  return (
    <>
      <Helmet>
        <title>About David Seibold - Full-Stack Engineer</title>
        <meta
          name="description"
          content="Learn about David Seibold, a full-stack engineer building TypeScript, Next.js, and Node.js products, with 11 years in healthcare M&A and select Solidity work."
        />
        <meta
          property="og:title"
          content="About David Seibold - Full-Stack Engineer"
        />
        <meta
          property="og:description"
          content="Background, skills, and expertise across full-stack engineering, data, and DeFi."
        />
        <meta
          property="og:image"
          content="https://daveyrockets.me/og-banner.png"
        />
        <meta property="og:url" content="https://daveyrockets.me/about" />
      </Helmet>

      <div className="flex flex-col items-center py-12 md:py-20 px-4 md:px-10 text-gray-800 dark:text-brand-cream">
        <div className="max-w-4xl w-full">
          <h1 className="text-4xl md:text-6xl text-center font-display font-bold mb-4">
            Hi, I'm {CONTACT_NAME}
          </h1>
          <p className="text-center text-lg text-gray-600 dark:text-brand-cream/80 mb-16 max-w-2xl mx-auto">
            I bring 11 years of M&A rigor to full-stack engineering — shipping
            TypeScript, Next.js, and Node.js products with tests, CI, and live
            demos you can click through.
          </p>

          {/* Timeline */}
          <RevealOnScroll className="mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-gradient text-center mb-10">
              My Journey
            </h2>
            <div className="relative border-l-2 border-brand-gold/40 ml-4 md:ml-6 space-y-10">
              {timeline.map((item) => (
                <div key={item.year} className="relative pl-8 md:pl-10">
                  <span className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-brand-gold border-2 border-white dark:border-brand-dark" />
                  <p className="text-sm font-semibold text-brand-gold mb-1">
                    {item.year}
                  </p>
                  <div className="flex items-center gap-2 mb-2">
                    <item.icon className="text-brand-gold" />
                    <h3 className="text-xl font-bold">{item.title}</h3>
                  </div>
                  <p className="text-gray-600 dark:text-brand-cream/80">
                    {item.description}
                  </p>
                </div>
              ))}
            </div>
          </RevealOnScroll>

          {/* Proof pillars */}
          <RevealOnScroll className="mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-gradient text-center mb-10">
              What I Bring
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {proofPillars.map((pillar) => (
                <div
                  key={pillar.title}
                  className="bg-gradient-to-br from-gray-50 to-white dark:from-brand-darker dark:to-[#2a2a2a] p-6 rounded-lg border border-gray-200 dark:border-brand-purple/20 hover:border-brand-gold/40 transition-all duration-300 text-center"
                >
                  <pillar.icon className="text-4xl text-brand-gold mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-brand-gold mb-2">
                    {pillar.title}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-brand-cream/80">
                    {pillar.body}
                  </p>
                </div>
              ))}
            </div>
          </RevealOnScroll>

          {/* Condensed skills — one grid instead of duplicating Home content */}
          <RevealOnScroll className="mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-gradient text-center mb-8">
              Core Stack
            </h2>
            <div className="flex flex-wrap justify-center gap-2">
              {[
                "TypeScript",
                "React",
                "Next.js",
                "Node.js",
                "Express",
                "PostgreSQL",
                "MongoDB",
                "Drizzle",
                "Auth.js",
                "Tailwind CSS",
                "Solidity",
                "Foundry",
                "Rust",
                "Anchor",
                "OpenAI / RAG",
              ].map((skill) => (
                <span
                  key={skill}
                  className="px-3 py-1 bg-purple-100 dark:bg-brand-purple/20 text-gray-700 dark:text-brand-cream rounded-md text-sm border border-purple-200 dark:border-brand-purple/30"
                >
                  {skill}
                </span>
              ))}
            </div>
            <p className="text-center mt-6 text-gray-600 dark:text-brand-cream/70 text-sm">
              See{" "}
              <Link to="/projects" className="text-brand-gold hover:underline">
                Projects
              </Link>{" "}
              for detailed case studies.
            </p>
          </RevealOnScroll>

          <div className="text-center pb-8">
            <EnhancedButton href="/contact" variant="primary" size="lg">
              Let's Connect
            </EnhancedButton>
            <p className="mt-4 text-sm text-gray-500 dark:text-brand-cream/60">
              Or email{" "}
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                className="text-brand-gold hover:underline"
              >
                {CONTACT_EMAIL}
              </a>
            </p>
          </div>
        </div>
      </div>
    </>
  );
}

export default About;
